import { useEffect, useRef, useState } from "react";
import { api, ApiError, type UserView } from "../api";
import { IconStar } from "../icons";
import { Overlay } from "../ui";

interface Props {
  user: UserView;
  onClose: () => void;
  onPaywall: () => void;
}

const MAX_SOURCE_BYTES = 6 * 1024 * 1024;

const TIPS = [
  "Раскрытая ладонь целиком в кадре",
  "Дневной свет, без вспышки",
  "Ведущая рука расскажет о настоящем, другая — о задатках",
];

/** Подписи этапов разбора, сменяются по кругу, пока ждём ответ сервера — реальная длительность не привязана к ним. */
const STAGES = ["Сверяю с твоей матрицей…", "Смотрю линию ума…", "Читаю линию сердца…", "Проверяю линию судьбы…"];
/** Минимальная длительность анимации сканирования — даже если сервер ответит быстрее, ждём это время ради эффекта. */
const MIN_SCAN_MS = 6000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Светящиеся линии поверх иллюстрации — «дорисовываются» через stroke-dashoffset, зацикленно. */
function PalmScanLines() {
  return (
    <svg className="palmhero-lines" viewBox="0 0 220 220" aria-hidden>
      <path className="palmhero-line l1" d="M70 170 C 90 130, 95 90, 110 40" />
      <path className="palmhero-line l2" d="M95 155 C 105 120, 120 95, 150 70" />
      <path className="palmhero-line l3" d="M80 140 C 100 125, 130 118, 160 110" />
    </svg>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      // data:image/jpeg;base64,xxxxx → берём только то, что после запятой
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

const ERROR_TEXT: Record<string, string> = {
  bad_image: "Не получилось разобрать фото — переснимите ладонь при хорошем освещении, так чтобы были видны линии.",
  not_onboarded: "Сначала заполните дату рождения в профиле.",
  ai_unavailable: "Разбор сейчас недоступен. Попробуйте чуть позже.",
  disabled: "Разбор ладони временно недоступен.",
};

/** Разбор ладони по фото — доступно на тарифе «Полный». Фото не сохраняется, только готовый текст. */
export function Palm({ user, onClose, onPaywall }: Props) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [handSide, setHandSide] = useState<"right" | "left">("right");
  const [leading, setLeading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [result, setResult] = useState<{ title: string; body: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<{ lines: { title: string; body: string }[]; createdAt: string }[] | null>(null);

  useEffect(() => {
    if (!busy) {
      setStageIdx(0);
      return;
    }
    const id = window.setInterval(() => setStageIdx((i) => (i + 1) % STAGES.length), 1500);
    return () => window.clearInterval(id);
  }, [busy]);

  const requireTier = () => {
    if (user.tier !== "full") {
      onPaywall();
      return false;
    }
    return true;
  };

  const pickCamera = () => requireTier() && cameraRef.current?.click();
  const pickGallery = () => requireTier() && galleryRef.current?.click();

  const openHistory = async () => {
    if (!requireTier()) return;
    setError(null);
    try {
      const r = await api.palmHistory();
      if (r.items.length === 0) {
        setError("Прошлых разборов пока нет — сделайте первое фото.");
        return;
      }
      setHistory(r.items);
      setResult(r.items[r.items.length - 1]!.lines);
    } catch {
      setError("Не удалось загрузить историю. Попробуйте ещё раз.");
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Нужно фото в формате JPEG, PNG или WebP.");
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError(
        "Файл слишком большой (лимит 6 МБ) — сделайте фото заново или выберите снимок меньшего размера. Если камера снимает в высоком качестве, попробуйте уменьшить разрешение в настройках камеры.",
      );
      return;
    }
    setError(null);
    setResult(null);
    setHistory(null);
    setBusy(true);
    try {
      const base64 = await fileToBase64(file);
      // Ждём не меньше MIN_SCAN_MS, даже если сервер ответит быстрее — иначе анимация сканирования мелькает и обрывается.
      const [r] = await Promise.all([api.readPalm(base64, file.type, handSide, leading), sleep(MIN_SCAN_MS)]);
      setResult(r.lines);
    } catch (e) {
      if (e instanceof ApiError && e.code === "tier_required") {
        onPaywall();
      } else {
        const code = e instanceof ApiError ? e.code : "";
        setError(ERROR_TEXT[code] ?? "Не удалось разобрать фото. Попробуйте ещё раз.");
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setHistory(null);
    setError(null);
  };

  return (
    <Overlay onClose={onClose} tone="dark">
      <div className="palmbody">
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void onFile(e)} />
        <input ref={galleryRef} type="file" accept="image/*" hidden onChange={(e) => void onFile(e)} />

        <div className={`palmhero${busy ? " palmhero-scanning" : " palmhero-idle"}`}>
          <img src="/assets/cards/palm-hand.png" alt="" className="palmhero-img" />
          {!busy && (
            <>
              <span className="dailycard-ambient a1" aria-hidden>✦</span>
              <span className="dailycard-ambient a2" aria-hidden>✦</span>
              <span className="dailycard-ambient a3" aria-hidden>✦</span>
            </>
          )}
          <span className="palmhero-scanline" aria-hidden />
          <PalmScanLines />
          <span className="palmhero-star s1" aria-hidden>✦</span>
          <span className="palmhero-star s2" aria-hidden>✦</span>
          <span className="palmhero-star s3" aria-hidden>✦</span>
        </div>

        <p className="chatintro-eyebrow">ХИРОМАНТИЯ</p>
        <h1 className="chatintro-title" style={{ marginBottom: 0 }}>
          Что написано
          <br />
          на твоей ладони
        </h1>

        {error && <p className="palmerror">{error}</p>}

        {!result && !busy && (
          <>
            <div className="palmtoggle">
              <button type="button" className={handSide === "right" ? "on" : ""} onClick={() => setHandSide("right")}>
                Правая рука
              </button>
              <button type="button" className={handSide === "left" ? "on" : ""} onClick={() => setHandSide("left")}>
                Левая рука
              </button>
            </div>

            <label className="palmcheck">
              <input type="checkbox" checked={leading} onChange={(e) => setLeading(e.target.checked)} />
              <span>Это моя ведущая рука</span>
            </label>

            <ul className="palmtips">
              {TIPS.map((t) => (
                <li key={t}>
                  <IconStar />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <p className="palmdisclaimer">
              Фото нужно только для разбора и нигде не сохраняется. Это инструмент для саморефлексии — не медицинская
              диагностика и не предсказание.
            </p>

            <button className="primary" style={{ marginTop: 8 }} onClick={pickCamera}>
              Сфотографировать ладонь
            </button>
            <button className="outline light" style={{ width: "100%", marginTop: 10 }} onClick={pickGallery}>
              Выбрать фото из галереи
            </button>
            <button className="outline light" style={{ width: "100%", marginTop: 10 }} onClick={() => void openHistory()}>
              Мой прошлый разбор
            </button>
          </>
        )}

        {busy && (
          <div className="palmstage">
            <span className="palmstage-icon" aria-hidden>
              <IconStar />
            </span>
            <span className="palmstage-text">
              <b>{STAGES[stageIdx]}</b>
              <small>Не закрывай приложение</small>
            </span>
          </div>
        )}

        {result && !busy && (
          <>
            {result.map((line) => (
              <div className="palmresult" key={line.title}>
                <h3>{line.title}</h3>
                <p style={{ whiteSpace: "pre-wrap" }}>{line.body}</p>
              </div>
            ))}
            {history && history.length > 1 && (
              <p className="palmdisclaimer">Показан последний из {history.length} разборов.</p>
            )}
            <button className="outline light" style={{ width: "100%", marginTop: 10 }} onClick={reset}>
              Разобрать новое фото
            </button>
          </>
        )}
      </div>
    </Overlay>
  );
}
